/**
 * TrainingAnalyticsScreen - Mobile preview for training analytics
 * Shows:
 * - Summary cards (calories, trainings, active days, streak)
 * - Area chart for calories over time
 * - Activity breakdown
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { ArrowLeft, Flame, Activity, Calendar, Award, Zap, ChevronDown, ChevronUp, Loader2, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const COLORS = ['#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];

export const TrainingAnalyticsScreen = ({ onNavigate, portalToken }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState(30);
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [portalToken, period]);

  const loadAnalytics = async () => {
    if (!portalToken) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/nutrition/training-log/analytics`, {
        params: { days: period },
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-950 min-h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-zinc-950 min-h-full p-4">
        <button onClick={() => onNavigate('nutrition')} className="flex items-center gap-2 text-white mb-4">
          <ArrowLeft size={20} />
          <span>Zurück</span>
        </button>
        <div className="text-center text-zinc-400 py-8">
          <p>Keine Daten verfügbar</p>
        </div>
      </div>
    );
  }

  const { summary, activity_breakdown, chart_data, epoc_enabled } = analytics;

  // Format chart data
  const formattedChartData = chart_data.map(d => ({
    ...d,
    date: d.date.slice(5), // MM-DD
    total: d.calories_burned + d.afterburn_received
  }));

  return (
    <div className="bg-zinc-950 min-h-full">
      {/* Header */}
      <div className="sticky top-0 bg-zinc-950/95 backdrop-blur-sm p-4 border-b border-zinc-800 z-10">
        <div className="flex items-center justify-between">
          <button onClick={() => onNavigate('nutrition')} className="flex items-center gap-2 text-white">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-white font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-orange-500" />
            Training Analytics
          </h1>
          <select
            value={period}
            onChange={(e) => setPeriod(parseInt(e.target.value))}
            className="bg-zinc-800 text-white text-xs rounded-lg px-2 py-1 border border-zinc-700"
          >
            <option value={7}>7 Tage</option>
            <option value={14}>14 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl p-3 border border-orange-500/30">
            <div className="flex items-center gap-1 text-orange-400 mb-1">
              <Flame className="w-3 h-3" />
              <span className="text-[10px]">Verbrannt</span>
            </div>
            <p className="text-xl font-bold text-white">{summary.total_calories_burned.toLocaleString()}</p>
            <p className="text-[10px] text-zinc-400">kcal gesamt</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-3 border border-blue-500/30">
            <div className="flex items-center gap-1 text-blue-400 mb-1">
              <Activity className="w-3 h-3" />
              <span className="text-[10px]">Trainings</span>
            </div>
            <p className="text-xl font-bold text-white">{summary.total_trainings}</p>
            <p className="text-[10px] text-zinc-400">in {period} Tagen</p>
          </div>

          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-3 border border-green-500/30">
            <div className="flex items-center gap-1 text-green-400 mb-1">
              <Calendar className="w-3 h-3" />
              <span className="text-[10px]">Aktive Tage</span>
            </div>
            <p className="text-xl font-bold text-white">{summary.active_days}</p>
            <p className="text-[10px] text-zinc-400">von {period}</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-3 border border-purple-500/30">
            <div className="flex items-center gap-1 text-purple-400 mb-1">
              <Award className="w-3 h-3" />
              <span className="text-[10px]">Streak</span>
            </div>
            <p className="text-xl font-bold text-white">{summary.current_streak}</p>
            <p className="text-[10px] text-zinc-400">Tage in Folge</p>
          </div>
        </div>

        {/* Afterburn Info */}
        {epoc_enabled && summary.total_afterburn_received > 0 && (
          <div className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 rounded-xl p-3 border border-orange-500/30 flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-full">
              <Zap className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-orange-300">Nachbrenn-Effekt erhalten</p>
              <p className="text-lg font-bold text-white">+{summary.total_afterburn_received.toLocaleString()} kcal</p>
            </div>
          </div>
        )}

        {/* Chart */}
        <div className="bg-zinc-900/50 rounded-xl p-3 border border-zinc-800">
          <h3 className="text-white text-sm font-medium mb-3 flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-500" />
            Kalorienverbrauch
          </h3>
          <div className="h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formattedChartData}>
                <defs>
                  <linearGradient id="colorCal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={8} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={8} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#18181b', 
                    border: '1px solid #3f3f46',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '10px'
                  }}
                  formatter={(value) => [`${value} kcal`]}
                />
                <Area 
                  type="monotone" 
                  dataKey="calories_burned" 
                  stroke="#f97316" 
                  fillOpacity={1}
                  fill="url(#colorCal)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Activity Breakdown */}
        {activity_breakdown.length > 0 && (
          <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
            <button
              onClick={() => setShowBreakdown(!showBreakdown)}
              className="w-full p-3 flex items-center justify-between text-left"
            >
              <span className="text-white text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-500" />
                Trainingsarten
              </span>
              {showBreakdown ? (
                <ChevronUp className="w-4 h-4 text-zinc-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-zinc-400" />
              )}
            </button>
            
            {showBreakdown && (
              <div className="p-3 pt-0 space-y-2">
                {/* Mini Pie Chart */}
                <div className="h-[120px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={activity_breakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={50}
                        paddingAngle={2}
                        dataKey="count"
                      >
                        {activity_breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Activity List */}
                {activity_breakdown.slice(0, 5).map((activity, index) => (
                  <div 
                    key={activity.type}
                    className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-xs text-white">{activity.label}</span>
                    </div>
                    <span className="text-xs text-zinc-400">{activity.count}x ({activity.percentage}%)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Additional Stats */}
        <div className="bg-zinc-900/50 rounded-xl p-3 border border-zinc-800 space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-zinc-400">Ø Kalorien pro Training</span>
            <span className="text-white">{summary.avg_calories_per_training} kcal</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-zinc-400">Ø Trainings pro Woche</span>
            <span className="text-white">{summary.avg_trainings_per_week}x</span>
          </div>
          {epoc_enabled && (
            <div className="flex justify-between text-xs">
              <span className="text-zinc-400">Nachbrenn-Modus</span>
              <span className="text-green-400">Aktiviert</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingAnalyticsScreen;
